import React, { useEffect, useState } from "react";

import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import AxiosInstance from "../../services/AxiosInstance";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import {
    CONFIGURATION_RELATIONSHIP_URL,
    CONFIGURATION_ITEM_URL
} from "../../Constants/Constants";

import { useAlert } from "../alerts/AlertContext";

const ConfigurationItemRelationshipDialog = ({
    configurationItemId = "",
    configurationItemName = "",
    variant = "icon"
}) => {

    const showAlert = useAlert();

    const [open, setOpen] = useState(false);

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [relationships, setRelationships] =
        useState([]);

    const [configurationItems, setConfigurationItems] =
        useState([]);

    const [selectedRelationshipId, setSelectedRelationshipId] =
        useState("");

    const [selectedItems, setSelectedItems] =
        useState([]);

    const [existingRelationships, setExistingRelationships] =
        useState([]);

    const loadData = async () => {

        setLoading(true);

        try {

            const [
                relationshipResponse,
                itemResponse,
                existingResponse
            ] = await Promise.all([

                AxiosInstance.get(
                    `${CONFIGURATION_RELATIONSHIP_URL}`
                ),

                AxiosInstance.get(
                    CONFIGURATION_ITEM_URL
                ),

                AxiosInstance.get(
                    `${CONFIGURATION_ITEM_URL}/${configurationItemId}/relationships`
                )
            ]);

            setRelationships(
                Array.isArray(relationshipResponse.data)
                    ? relationshipResponse.data
                    : []
            );

            setConfigurationItems(
                Array.isArray(itemResponse.data)
                    ? itemResponse.data
                    : []
            );

            setExistingRelationships(
                Array.isArray(existingResponse.data)
                    ? existingResponse.data
                    : []
            );

        } catch (error) {

            console.error(
                "Unable to load configuration relationships",
                error
            );

            showAlert(
                "Unable to load configuration relationships",
                "error"
            );

        } finally {
            setLoading(false);
        }
    };


    const handleOpen = () => {

        setOpen(true);

        loadData();
    };


    const handleClose = () => {

        if (saving) {
            return;
        }

        setOpen(false);

        setSelectedRelationshipId("");

        setSelectedItems([]);
    };


    const handleSave = async () => {

        if (!selectedRelationshipId) {

            showAlert(
                "Select a relationship",
                "error"
            );

            return;
        }

        if (!selectedItems.length) {

            showAlert(
                "Select at least one Configuration Item",
                "error"
            );

            return;
        }

        setSaving(true);

        try {

            /*
             * Create each selected relationship.
             */

            for (const item of selectedItems) {

                await AxiosInstance.post(
                    `${CONFIGURATION_ITEM_URL}/${configurationItemId}/relationships`,
                    {
                        relatedConfigurationItemId:
                            item.id,

                        configurationRelationshipId:
                            Number(selectedRelationshipId)
                    }
                );
            }

            showAlert(
                "Configuration relationships saved successfully",
                "success"
            );

            setSelectedRelationshipId("");

            setSelectedItems([]);

            await loadData();

        } catch (error) {

            console.error(
                "Unable to save relationships",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to save configuration relationships",
                "error"
            );

        } finally {

            setSaving(false);
        }
    };


    const handleDelete = async (id) => {

        try {

            await AxiosInstance.delete(
                `${CONFIGURATION_ITEM_URL}/relationships/${id}`
            );

            setExistingRelationships(prev =>
                prev.filter(
                    item =>
                        item.id !== id
                )
            );

            showAlert(
                "Relationship removed",
                "success"
            );

        } catch (error) {

            console.error(
                "Unable to delete relationship",
                error
            );

            showAlert(
                "Unable to remove relationship",
                "error"
            );
        }
    };


    /*
     * Don't allow the current CI to be selected.
     */
    const availableItems =
        configurationItems.filter(
            item =>
                String(item.id) !==
                String(configurationItemId)
        );


    return (
        <><ThemeProvider>
            <Tooltip title="Configuration Relationships">

                <IconButton
                    onClick={handleOpen}
                    size="small"
                    color="primary"
                >
                    <AccountTreeOutlinedIcon />
                </IconButton>

            </Tooltip>


            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
            >

                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}
                >

                    <Box>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            Configuration Relationships
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            {configurationItemName}
                        </Typography>

                    </Box>

                    <IconButton
                        onClick={handleClose}
                        disabled={saving}
                    >
                        <CloseIcon />
                    </IconButton>

                </DialogTitle>


                <DialogContent dividers>

                    {loading ? (

                        <Box
                            sx={{
                                minHeight: 250,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}
                        >
                            <CircularProgress />
                        </Box>

                    ) : (

                        <Stack spacing={3}>

                            {/* Relationship */}

                            <FormControl fullWidth>

                                <InputLabel>
                                    Relationship
                                </InputLabel>

                                <Select
                                    value={
                                        selectedRelationshipId
                                    }
                                    label="Relationship"
                                    onChange={(event) =>
                                        setSelectedRelationshipId(
                                            event.target.value
                                        )
                                    }
                                >

                                    {relationships.map(
                                        relationship => (

                                            <MenuItem
                                                key={
                                                    relationship.id
                                                }
                                                value={
                                                    relationship.id
                                                }
                                            >
                                                {
                                                    relationship.name
                                                }
                                            </MenuItem>

                                        )
                                    )}

                                </Select>

                            </FormControl>


                            {/* Related CIs */}

                            <Autocomplete
                                multiple
                                options={
                                    availableItems
                                }
                                value={
                                    selectedItems
                                }
                                onChange={(
                                    event,
                                    newValue
                                ) =>
                                    setSelectedItems(
                                        newValue
                                    )
                                }
                                isOptionEqualToValue={(
                                    option,
                                    value
                                ) =>
                                    String(option.id) ===
                                    String(value.id)
                                }
                                getOptionLabel={
                                    option =>
                                        option?.name ||
                                        ""
                                }
                                renderOption={(
                                    props,
                                    option,
                                    { selected }
                                ) => (

                                    <li
                                        {...props}
                                        key={option.id}
                                    >

                                        <Checkbox
                                            checked={
                                                selected
                                            }
                                        />

                                        <ListItemText
                                            primary={
                                                option.name
                                            }
                                            secondary={
                                                `CI ID: ${option.id}`
                                            }
                                        />

                                    </li>
                                )}
                                renderInput={
                                    params => (

                                        <TextField
                                            {...params}
                                            label="Related Configuration Items"
                                            placeholder="Search configuration items..."
                                        />

                                    )
                                }
                            />


                            {/* Existing */}

                            <Box>

                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    sx={{ mb: 1 }}
                                >
                                    Existing Relationships
                                </Typography>


                                {existingRelationships.length === 0 ? (

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No relationships configured.
                                    </Typography>

                                ) : (

                                    <Stack spacing={1}>

                                        {existingRelationships.map(
                                            relationship => (

                                                <Box
                                                    key={
                                                        relationship.id
                                                    }
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "space-between",
                                                        border: "1px solid",
                                                        borderColor:
                                                            "divider",
                                                        borderRadius: 1,
                                                        px: 2,
                                                        py: 1.2
                                                    }}
                                                >

                                                    <Box>

                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                        >
                                                            {
                                                                relationship
                                                                    .relatedConfigurationItemName
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {
                                                                relationship
                                                                    .configurationRelationshipName
                                                            }
                                                        </Typography>

                                                    </Box>


                                                    <Tooltip title="Remove Relationship">

                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    relationship.id
                                                                )
                                                            }
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>

                                                    </Tooltip>

                                                </Box>

                                            )
                                        )}

                                    </Stack>
                                )}

                            </Box>

                        </Stack>
                    )}

                </DialogContent>


                <DialogActions>

                    <Button
                        onClick={handleClose}
                        disabled={saving}
                    >
                        Close
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={
                            saving ||
                            loading ||
                            !selectedRelationshipId ||
                            selectedItems.length === 0
                        }
                    >
                        {saving
                            ? "Saving..."
                            : "Save Relationship"}
                    </Button>

                </DialogActions>

            </Dialog>
        </ThemeProvider>
        </>
    );
};

export default ConfigurationItemRelationshipDialog;